import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TinyCard, CardHoverPreview } from "@/components/Card3D";
import { SelectedCardBar } from "@/components/SelectedCardBar";
import { motion } from "framer-motion";
import { Flag, Rocket, Trophy, Heart, Wind, Swords, Shield, Users, ChevronRight, ChevronLeft, Search, DoorOpen, Gift, Skull, AlertTriangle, Check, Tv, Coins, Eye } from "lucide-react";
import { toast } from "sonner";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const SORTS = { spd: "Speed", hp: "Health", atk: "Attack", title: "Name" };
const PAGE = 12;

function StatPip({ icon: Icon, label, value, color }) {
  return <span className="inline-flex items-center gap-1 font-mono text-[10px]" style={{ color }}><Icon className="h-3 w-3" /> {label} {value}</span>;
}

// Spectator Pit — AI-only races anyone can watch and bet on (pari-mutuel pools).
function SpectatorPit({ navigate }) {
  const [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["spectator-races"],
    queryFn: async () => (await api.get("/race/spectator/list")).data,
    refetchInterval: 4000,
  });
  const start = async () => {
    setBusy(true);
    try { const r = await api.post("/race/spectator/create"); navigate(`/race/watch/${r.data.id}`); }
    catch (e) { toast.error(apiError(e, "Couldn't open a race")); }
    finally { setBusy(false); }
  };
  const live = data?.live || [];
  return (
    <div className="hud-pill rounded-2xl p-5" data-testid="spectator-pit">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-300/80 flex items-center gap-1.5"><Tv className="h-3.5 w-3.5" /> Spectator Pit</h3>
        <Button size="sm" data-testid="spectator-new-race" disabled={busy} onClick={start} className="h-7 bg-emerald-400 text-black hover:bg-emerald-300 text-[11px]">
          <Rocket className="h-3.5 w-3.5 mr-1" /> New AI race
        </Button>
      </div>
      <p className="text-[11px] text-white/40 mb-2">No card needed — back which AI racer wins 1st and split the pot.</p>
      {live.length === 0 ? (
        <p className="text-[12px] text-white/40">No live races yet — hit “New AI race” to start one.</p>
      ) : (
        <div className="space-y-2">
          {live.map((r) => (
            <button key={r.id} data-testid={`spectator-race-${r.id}`} onClick={() => navigate(`/race/watch/${r.id}`)}
              className="w-full flex items-center gap-2 rounded-lg bg-white/[0.03] px-2 py-1.5 hover:bg-white/[0.07] text-left transition-colors">
              <div className="flex -space-x-2 shrink-0">
                {r.racers.slice(0, 5).map((p) => (
                  <div key={p.idx} className={`h-7 w-5 rounded overflow-hidden bg-black border border-black rarity-${p.rarity}`}>
                    {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs flex items-center gap-1.5">
                  {r.status === "betting" ? <span className="text-emerald-300">Bets open</span> : <span className="text-amber-300 inline-flex items-center gap-1">● Live</span>}
                </div>
                <div className="font-mono text-[10px] text-amber-300 inline-flex items-center gap-1"><Coins className="h-3 w-3" /> {r.total_pool.toLocaleString()}c pot</div>
              </div>
              <Eye className="h-4 w-4 text-white/30 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Multi-edition chooser — each edition has its own rolled stats, so the player
// picks the exact runner. Mirrors the Playden stack-flipper, with hover previews.
function RunnerEditionPicker({ group, selected, onPick, onClose }) {
  if (!group) return null;
  const sorted = [...group.cards].sort((a, b) => (a.edition_number || 0) - (b.edition_number || 0));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md max-h-[85vh] overflow-y-auto" data-testid="runner-edition-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-[0.06em]">{group.rep.title}</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-white/50 -mt-1 mb-1">{sorted.length} editions owned — each has its own rolled stats. Pick your runner.</p>
        <div className="space-y-2">
          {sorted.map((c) => {
            const a = c.arena || {};
            const isSel = c.id === selected;
            return (
              <CardHoverPreview key={c.id} card={c} side="right">
                <div role="button" tabIndex={0} data-testid={`race-edition-${c.id}`}
                  onClick={() => onPick(c.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") onPick(c.id); }}
                  className={`hud-pill rounded-xl p-3 flex items-center justify-between gap-3 cursor-pointer transition-colors duration-150 outline-none ${isSel ? "border-amber-300 bg-amber-300/10" : "hover:bg-white/5 hover:border-cyan-400/30"}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white">Edition #{c.edition_number ?? "—"}</div>
                    <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1">
                      <StatPip icon={Wind} label="SPD" value={a.spd ?? "—"} color="#22d3ee" />
                      <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                      <StatPip icon={Swords} label="ATK" value={a.atk ?? "—"} color="#fbbf24" />
                      <StatPip icon={Shield} label="DEF" value={a.def ?? "—"} color="#a3e635" />
                    </div>
                  </div>
                  <Button size="sm" className={isSel ? "bg-emerald-400 text-black hover:bg-emerald-300 shrink-0" : "bg-amber-300 text-black hover:bg-amber-200 shrink-0"}>
                    {isSel ? <><Check className="h-3.5 w-3.5 mr-1" /> Selected</> : "Race this"}
                  </Button>
                </div>
              </CardHoverPreview>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RaceLobbyPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [editionPick, setEditionPick] = useState(null);
  const [slots, setSlots] = useState(4);
  const [buyIn, setBuyIn] = useState(0);
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [sort, setSort] = useState("spd");
  const [page, setPage] = useState(0);

  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data });
  const { data: openRaces } = useQuery({ queryKey: ["open-races"], queryFn: async () => (await api.get("/race/open")).data, refetchInterval: 4000 });
  const { data: history } = useQuery({ queryKey: ["my-races"], queryFn: async () => (await api.get("/race")).data });

  // Group duplicate editions of the same template into one stacked tile, exactly
  // like the Playden / Explore views (overlap visual + count badge + hover preview).
  const groups = useMemo(() => {
    let list = [...(cards || [])];
    if (search.trim()) list = list.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase()));
    if (rarity !== "all") list = list.filter((c) => c.rarity === rarity);
    const map = new Map();
    for (const c of list) {
      const key = c.template_id || c.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    const gs = [...map.values()].map((cs) => ({ key: cs[0].template_id || cs[0].id, cards: cs, rep: cs[0] }));
    gs.sort((a, b) => sort === "title" ? (a.rep.title || "").localeCompare(b.rep.title || "") : ((b.rep.arena?.[sort] ?? 0) - (a.rep.arena?.[sort] ?? 0)));
    return gs;
  }, [cards, search, rarity, sort]);

  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = groups.slice(safePage * PAGE, safePage * PAGE + PAGE);

  const create = useMutation({
    mutationFn: async () => (await api.post("/race/create", { card_id: selected, slots, buy_in: buyIn })).data,
    onSuccess: (r) => navigate(`/race/${r.id}`),
    onError: (e) => toast.error(apiError(e, "Couldn't open the lobby")),
  });
  const join = useMutation({
    mutationFn: async (raceId) => (await api.post(`/race/${raceId}/join`, { card_id: selected })).data,
    onSuccess: (r) => navigate(`/race/${r.id}`),
    onError: (e) => toast.error(apiError(e, "Couldn't join")),
  });

  const pickGroup = (g) => {
    if (g.cards.length > 1) setEditionPick(g);
    else setSelected(g.rep.id);
  };

  const selectedCard = (cards || []).find((c) => c.id === selected);

  const hostBlock = (
    <div className="hud-pill rounded-2xl p-5">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Host a race</h3>
      <div className="grid grid-cols-5 gap-1.5">
        {[2, 3, 4, 5, 6].map((n) => (
          <button key={n} data-testid={`race-slots-${n}`} onClick={() => setSlots(n)} className={`py-2 rounded-lg font-mono text-sm transition-colors ${slots === n ? "bg-amber-300 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{n}</button>
        ))}
      </div>
      <p className="text-[11px] text-white/40 mt-2">{slots} total racers. Empty seats fill with power-matched AI after 60s.</p>
      <div className="mt-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300/70 mb-1.5 flex items-center gap-1.5"><Coins className="h-3 w-3" /> Buy-in pot — winner takes all</div>
        <div className="grid grid-cols-5 gap-1.5">
          {[0, 50, 100, 250, 500].map((n) => (
            <button key={n} data-testid={`race-buyin-${n}`} onClick={() => setBuyIn(n)} className={`py-2 rounded-lg font-mono text-xs transition-colors ${buyIn === n ? "bg-emerald-400 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{n === 0 ? "Free" : n}</button>
          ))}
        </div>
        <p className="text-[11px] text-white/40 mt-1.5">{buyIn === 0 ? "No ante — race for the card stakes only." : `Each human racer antes ${buyIn.toLocaleString()}c. The best-placed human takes the whole pot.`}</p>
      </div>
      <Button data-testid="race-create-button" disabled={!selected || create.isPending} onClick={() => create.mutate()} className="w-full mt-3 bg-amber-300 text-black hover:bg-amber-200 disabled:opacity-40 font-medium">
        <Rocket className="h-4 w-4 mr-1.5" /> {create.isPending ? "Opening lobby…" : (buyIn > 0 ? `Open Lobby · ${buyIn.toLocaleString()}c` : "Open Lobby")}
      </Button>
      {!selected && <p className="text-[11px] text-white/35 mt-2 text-center">Pick a runner first.</p>}
    </div>
  );

  const joinBlock = (
    <div className="hud-pill rounded-2xl p-5">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" /> Join an open lobby</h3>
      {(openRaces || []).length > 0 && !selected && (
        <p className="text-[11px] text-amber-300/80 mb-2" data-testid="race-join-hint">↞ Pick a runner first, then Join.</p>
      )}
      {(openRaces || []).length === 0 ? (
        <p className="text-[12px] text-white/40">No open lobbies right now. Host one!</p>
      ) : (
        <div className="space-y-2">
          {openRaces.map((r) => (
            <div key={r.id} data-testid={`open-race-${r.id}`} className="flex items-center gap-2 rounded-lg bg-white/[0.03] px-2 py-1.5">
              <div className="flex -space-x-2">
                {r.racers.slice(0, 4).map((p) => (
                  <div key={p.idx} className={`h-7 w-5 rounded overflow-hidden bg-black border border-black rarity-${p.rarity}`}>
                    {p.card?.front_image && <img src={p.card.front_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs flex items-center gap-2">
                  {r.racers.length}/{r.slots}
                  {r.buy_in > 0 && <span className="font-mono text-[10px] text-emerald-300 inline-flex items-center gap-0.5" data-testid={`open-race-pot-${r.id}`}><Coins className="h-3 w-3" /> {r.pot.toLocaleString()}c pot</span>}
                  {r.total_pool > 0 && <span className="font-mono text-[10px] text-cyan-300 inline-flex items-center gap-0.5"><Eye className="h-3 w-3" /> {r.total_pool.toLocaleString()}c bet</span>}
                </div>
                <div className="font-mono text-[10px] text-amber-300">{r.seconds_open_left}s left{r.buy_in > 0 ? ` · ${r.buy_in.toLocaleString()}c ante` : ""}</div>
              </div>
              <Button size="sm" variant="ghost" data-testid={`watch-race-${r.id}`} onClick={() => navigate(`/race/${r.id}`)} className="text-cyan-300 h-7 px-2" title="Watch & bet"><Eye className="h-4 w-4" /></Button>
              {r.joined ? (
                <Button size="sm" variant="ghost" onClick={() => navigate(`/race/${r.id}`)} className="text-cyan-300 h-7">Open</Button>
              ) : (
                <Button size="sm" data-testid={`join-race-${r.id}`} disabled={join.isPending} onClick={() => selected ? join.mutate(r.id) : toast.error("Pick a runner first")} className="bg-cyan-400 text-black hover:bg-cyan-300 h-7 disabled:opacity-40">{r.buy_in > 0 ? `Join · ${r.buy_in.toLocaleString()}c` : "Join"}</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const recentBlock = (
    <div className="hud-pill rounded-2xl p-5">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" /> Recent runs</h3>
      {(history || []).length === 0 ? (
        <p className="text-[12px] text-white/40">No races yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {history.slice(0, 6).map((h) => (
            <button key={h.id} onClick={() => navigate(`/race/${h.id}`)} className="w-full flex items-center gap-2 text-left rounded-lg px-2 py-1.5 hover:bg-white/5">
              <div className={`h-8 w-6 rounded overflow-hidden bg-black shrink-0 rarity-${h.runner?.rarity}`}>{h.runner?.front_image && <img src={h.runner.front_image} alt="" className="w-full h-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs truncate">{h.runner?.title || "Race"}</div>
                <div className="font-mono text-[10px] text-white/40 flex items-center gap-1">
                  {h.status === "finished" ? <>P{h.player_place}{(h.results?.prize || h.results?.gear || h.results?.consumable) && h.player_place === 1 && <Gift className="h-3 w-3 text-emerald-300" />}{h.results?.forfeit && h.player_place > 1 && <Skull className="h-3 w-3 text-red-400" />}</> : h.status}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-white/30" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10" data-testid="race-lobby">
      <div className="flex items-center gap-2.5 sm:gap-3 mb-1">
        <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-lg sm:rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center shrink-0"><Flag className="h-5 w-5 sm:h-6 sm:w-6 text-amber-300" /></div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-white tracking-[0.04em] leading-[1.05]">THE GAUNTLET</h1>
          <p className="text-white/50 text-[12.5px] sm:text-sm leading-snug mt-0.5">Race speed = <span className="text-amber-300">SPD × current HP%</span>. Attacks slow rivals — the farther they are, the harder they're hit.</p>
        </div>
      </div>

      {/* Consequences — the one and only warning. */}
      <div data-testid="race-stakes-warning" className="mt-4 sm:mt-5 rounded-xl sm:rounded-2xl border border-amber-400/40 bg-gradient-to-r from-red-500/[0.12] via-amber-400/[0.10] to-emerald-500/[0.12] p-3 sm:p-4 flex items-start gap-2.5 sm:gap-3">
        <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-amber-300 shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <span className="font-display tracking-[0.06em] text-white text-base">REAL STAKES — this is your only warning.</span>
          <div className="mt-1 text-white/80">
            <span data-testid="race-forfeit-rule"><span className="text-red-300 font-semibold">Finish LAST in a paid race</span> and your runner is forfeited to the store, with a 15-minute owner-only recovery window. Shared-code cards remain owned but permanently locked out.</span>
            {" "}
            <span data-testid="race-win-reward-rule"><span className="text-emerald-300 font-semibold">Competitive 1st place earns gear or a consumable</span>—no new cards or credit rewards. The buy-in pot goes to the best-placed human, using only human stakes. AI contributes zero. Free races are practice.</span>
          </div>
        </div>
      </div>

      {selected ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6" data-testid="race-selected-layout">
          <SelectedCardBar card={selectedCard} onClear={() => setSelected(null)} testid="race-selected" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-5 space-y-4"><SpectatorPit navigate={navigate} />{hostBlock}</div>
            <div className="lg:col-span-7">{joinBlock}</div>
            <div className="lg:col-span-12">{recentBlock}</div>
          </div>
        </motion.div>
      ) : (
      <div className="grid lg:grid-cols-[1fr_340px] gap-8 mt-8">
        {/* Runner picker with filters + pagination */}
        <div>
          <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mr-auto">Choose your runner</h2>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <Input data-testid="race-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search…" className="h-8 w-36 pl-7 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
            </div>
            <Select value={rarity} onValueChange={(v) => { setRarity(v); setPage(0); }}>
              <SelectTrigger data-testid="race-rarity-filter" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{RARITIES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="race-sort" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {groups.length === 0 ? (
            <div className="hud-pill rounded-2xl p-10 text-center text-white/45">No cards match. Grab some from the Store first.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {shown.map((g) => {
                  const sel = g.cards.find((c) => c.id === selected);
                  const statCard = sel || g.rep;
                  const a = statCard.arena || {};
                  const isSel = !!sel;
                  return (
                    <div key={g.key} data-testid={`race-pick-${g.rep.id}`}
                      className={`rounded-xl transition-transform ${isSel ? "ring-2 ring-amber-300 ring-offset-2 ring-offset-[hsl(220_22%_6%)] scale-[1.02]" : ""}`}>
                      <TinyCard
                        card={statCard}
                        count={g.cards.length}
                        idKey={g.key}
                        previewSide="top"
                        onClick={() => pickGroup(g)}
                        footer={
                          <div className="mt-1 px-0.5">
                            <div className="text-white text-[11px] font-medium truncate flex items-center gap-1">
                              {isSel && <Check className="h-3 w-3 text-amber-300 shrink-0" />}{statCard.title}
                            </div>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                              <StatPip icon={Wind} label="SPD" value={a.spd ?? "—"} color="#22d3ee" />
                              <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                            </div>
                          </div>
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-[10px] text-white/35">{groups.length} runner{groups.length === 1 ? "" : "s"} · page {safePage + 1}/{pageCount}</span>
                <div className="flex gap-2">
                  <Button data-testid="race-prev-page" size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button data-testid="race-next-page" size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
          </>
        </div>

        {/* Create + Join */}
        <div className="space-y-4">
          <SpectatorPit navigate={navigate} />
          {hostBlock}
          {joinBlock}
          {recentBlock}
        </div>
      </div>
      )}

      <RunnerEditionPicker
        group={editionPick}
        selected={selected}
        onPick={(id) => { setSelected(id); setEditionPick(null); }}
        onClose={() => setEditionPick(null)}
      />
    </div>
  );
}
